import { useState, useMemo } from "react";
import { ChevronRight, ChevronDown, ChevronLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { HydratedItem } from "@forgepoint/shared";
import { ReviewStatusIndicator } from "@/modules/quotes/components/ReviewStatusIndicator";
import { Badge } from "@/components/ui/badge";

/**
 * Props for the AssemblyTreeSidebar component.
 * Renders a collapsible sidebar showing the full component hierarchy of an assembly.
 */
interface AssemblyTreeSidebarProps {
  rootItem: HydratedItem;
  selectedItemId: string;
  onSelectItem: (itemId: string) => void;
  onMarkReviewed: (itemId: string) => void;
}

/**
 * A collapsible sidebar that displays an assembly's component tree with review status indicators.
 * Defaults to collapsed and expands on hover or click.
 */
export function AssemblyTreeSidebar({
  rootItem,
  selectedItemId,
  onSelectItem,
  onMarkReviewed,
}: AssemblyTreeSidebarProps) {
  const { reviewedCount, totalCount } = useMemo(
    () => countReviewProgress(rootItem),
    [rootItem],
  );
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPinned, setIsPinned] = useState(false);

  const handleToggle = () => {
    const next = !isExpanded;
    setIsExpanded(next);
    setIsPinned(next);
  };

  return (
    <div
      className={cn(
        "flex flex-col h-full border-r bg-muted/5 overflow-hidden transition-all duration-300 ease-in-out flex-shrink-0",
        isExpanded ? "w-[clamp(8rem,25%,16rem)]" : "w-12",
      )}
      onMouseEnter={() => {
        if (!isPinned) {
          setIsExpanded(true);
        }
      }}
      onMouseLeave={() => {
        if (!isPinned) {
          setIsExpanded(false);
        }
      }}
    >
      {!isExpanded && (
        <button
          type="button"
          onClick={handleToggle}
          className="flex flex-col items-center gap-2 pt-3 pb-2 w-full hover:bg-accent/50 transition-colors"
        >
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
          <span className="text-[10px] text-muted-foreground font-medium [writing-mode:vertical-lr]">
            Assembly{totalCount > 0 ? ` (${reviewedCount}/${totalCount})` : ""}
          </span>
        </button>
      )}

      {isExpanded && (
        <>
          <div className="px-3 py-2 border-b flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">
              Assembly
            </span>
            <button
              type="button"
              onClick={handleToggle}
              className="p-0.5 rounded hover:bg-accent transition-colors"
            >
              <ChevronLeft className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          </div>
          <div className="flex-1 overflow-auto">
            <div className="p-2 space-y-0.5">
              <TreeNode
                item={rootItem}
                depth={0}
                selectedItemId={selectedItemId}
                onSelectItem={onSelectItem}
                onMarkReviewed={onMarkReviewed}
              />
            </div>
          </div>
          {totalCount > 0 && (
            <div className="px-3 py-2 border-t text-xs text-muted-foreground">
              {reviewedCount}/{totalCount} reviewed
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Props for a single tree node in the assembly hierarchy. */
interface TreeNodeProps {
  item: HydratedItem;
  depth: number;
  selectedItemId: string;
  onSelectItem: (itemId: string) => void;
  onMarkReviewed: (itemId: string) => void;
}

/** Renders a single node in the assembly tree with expand/collapse and review status. */
function TreeNode({
  item,
  depth,
  selectedItemId,
  onSelectItem,
  onMarkReviewed,
}: TreeNodeProps) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = item.components && item.components.length > 0;
  const isSelected = item.id === selectedItemId;

  return (
    <div>
      <div
        role="treeitem"
        tabIndex={0}
        onClick={() => onSelectItem(item.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelectItem(item.id);
          }
        }}
        className={cn(
          "w-full text-left px-2 py-1.5 rounded-md transition-colors flex items-center gap-1.5 cursor-pointer",
          "hover:bg-accent/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          isSelected && "bg-accent font-semibold",
        )}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            className="flex-shrink-0 p-0.5 hover:bg-accent rounded"
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            {expanded ? (
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
            )}
          </button>
        ) : (
          <span className="w-4 flex-shrink-0" />
        )}
        <ReviewStatusIndicator
          status={item.reviewStatus ?? null}
          size="sm"
          showReviewed
          onClick={
            item.reviewStatus === "NEEDS_REVIEW" &&
            !item.externalMappings?.length
              ? () => onMarkReviewed(item.id)
              : undefined
          }
        />
        <span className="text-xs truncate flex-1 min-w-0">{item.name}</span>
        <Badge
          variant="outline"
          className="text-[10px] px-1 py-0 flex-shrink-0"
        >
          {item.type === "MANUFACTURED"
            ? "MFG"
            : item.type === "PURCHASED"
              ? "PUR"
              : "MAT"}
        </Badge>
      </div>
      {hasChildren && expanded && (
        <div role="group">
          {item.components.map((comp) => (
            <TreeNode
              key={comp.component.id}
              item={comp.component}
              depth={depth + 1}
              selectedItemId={selectedItemId}
              onSelectItem={onSelectItem}
              onMarkReviewed={onMarkReviewed}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Counts reviewed and total items with a review status in the assembly tree.
 * Only counts items that have a non-null reviewStatus (agent-touched items).
 */
function countReviewProgress(item: HydratedItem): {
  reviewedCount: number;
  totalCount: number;
} {
  const isTracked =
    item.reviewStatus !== null && item.reviewStatus !== undefined;
  const isReviewed = item.reviewStatus === "REVIEWED";

  const childCounts = (item.components ?? []).reduce(
    (acc, comp) => {
      const child = countReviewProgress(comp.component);
      return {
        reviewedCount: acc.reviewedCount + child.reviewedCount,
        totalCount: acc.totalCount + child.totalCount,
      };
    },
    { reviewedCount: 0, totalCount: 0 },
  );

  return {
    reviewedCount: childCounts.reviewedCount + (isReviewed ? 1 : 0),
    totalCount: childCounts.totalCount + (isTracked ? 1 : 0),
  };
}
